import { baseUrl } from "../../baseUrl";
import { useQuery } from "@tanstack/react-query";
import { callTableColumnDefs } from "./callTableColumns";
import { UploadListModal } from "./modals/uploadListModal";
import { fetchWithAuthHandling } from "../../authHandling";
// import { checkCallAvailability } from "./checkCallAvailability";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  SystemUpdateAlt,
  // UploadFile
} from "@mui/icons-material";
import type { GridApi, GridReadyEvent } from "ag-grid-enterprise";
import { FormSubmissionTable } from "../common/formSubmissionTable";
import { UploadListErrorModal } from "./modals/uploadListErrorModal";
import {
  isValidPhoneNumber,
  formatPhoneNumberE164,
} from "../../utils/phoneValidator";
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
} from "@mui/material";

interface SortModel {
  colId: string;
  sort: "asc" | "desc";
}
interface CallData {
  calls: Array<Record<string, unknown>>;
}

export function Calls() {
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openUploadErrorDialog, setOpenUploadErrorDialog] = useState(false);

  const [filters] = useState({});
  const [sortModel] = useState<SortModel[]>([]);
  const [page, _] = useState(1);
  const [pageSize, setPageSize] = useState(2000);
  const [prevData, setPrevData] = useState<CallData | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridColumnApiRef = useRef<GridApi>(null);

  const { data, isLoading, error, isSuccess } = useQuery({
    queryKey: ["callsTable", { filters, sortModel, page, pageSize }],
    queryFn: async () => {
      const hasFilters = Object.keys(filters).length > 0;
      const hasSorting = sortModel.length > 0;

      if (hasFilters || hasSorting) {
        const body: {
          page: number;
          limit: number;
          query: Record<string, unknown>;
        } = {
          page,
          limit: pageSize,
          query: {}
        };

        if (hasFilters) {
          const filterFields: Record<string, string> = {};
          for (const [field, filter] of Object.entries(
            filters as Record<string, { filter: string }>
          )) {
            const filterValue = filter.filter;

            if (field === "emailAddress") {
              setPageSize(1);
            }
            if (filterValue) {
              if (field === "phoneNumber") {
                try {
                  const formattedPhoneNumber = isValidPhoneNumber(
                    filterValue,
                    "CA"
                  )
                    ? formatPhoneNumberE164(filterValue, "CA")
                    : formatPhoneNumberE164(filterValue, "US")
                      ? formatPhoneNumberE164(filterValue, "US")
                      : filterValue;
                  filterFields[field] = formattedPhoneNumber;
                } catch {
                  filterFields[field] = "";
                }
              } else {
                filterFields[field] = filterValue;
              }
            }
          }
          body.query.filter = filterFields;
        }

        if (hasSorting) {
          const firstSort = sortModel[0] as {
            colId: string;
            sort: "asc" | "desc";
          };

          const sortField = firstSort.colId;
          const sortOrder = firstSort.sort === "asc" ? "ASC" : "DESC";

          body.query.sort = {
            order: sortOrder,
            field: sortField
          };
        }

        const response = await fetchWithAuthHandling(
          `${baseUrl}/calls`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body)
          }
        );
        setPageSize(500);
        return response.json();
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });
      const response = await fetchWithAuthHandling(
        `${baseUrl}/calls?${params.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );

      return response.json();
    },
    retry: false
  });

  useEffect(() => {
    if (isSuccess) {
      setPrevData(data);
    }
  }, [isSuccess, data]);

  // const onFilterChanged = useCallback(() => {
  //   const filterModel = gridApiRef.current?.getFilterModel();
  //   setFilters(filterModel || {});
  // }, []);

  // const onSortChanged = useCallback(() => {
  //   if (gridColumnApiRef.current) {
  //     const sortModel = gridColumnApiRef.current
  //       .getColumnState()
  //       .filter((col: ColumnState) => col.sort !== null)
  //       .map((col: ColumnState) => ({
  //         colId: col.colId,
  //         sort: col.sort as "asc" | "desc"
  //       }));
  //     setSortModel(sortModel);
  //   }
  // }, []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    gridColumnApiRef.current = params.api;
  }, []);

  const exportToCsv = () => {
    if (gridApiRef.current) {
      gridApiRef.current.exportDataAsCsv({
        fileName: "exported-table.csv",
        processHeaderCallback: (params) => {
          const toPascalCase = (str: string) => {
            return str
              .replace(/-/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join("");
          };
          return toPascalCase(params.column.getColDef().headerName || "");
        }
      });
    }
  };

  const rowData = error ? prevData?.calls : data?.calls;

  if (!data && !isLoading) {
    return (
      <Container
        sx={{
          my: 5,
          maxWidth: {
            xs: "97%"
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center"
          }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Data Found
          </Typography>
        </Box>
      </Container>
    );
  }

  // const handleOpenUpladDialog = async () => {
  //   const response = await checkCallAvailability()
  //   if (!response) {
  //     setOpenUploadErrorDialog(true);
  //   } else {
  //     setOpenUploadDialog(true);
  //   }
  // }

  return (
    <Container
      sx={{
        mt: 3,
        maxWidth: {
          xs: "100%"
        }
      }}
      disableGutters={true}
    >
      {error && <Typography>Error loading data: {error.message}</Typography>}

      {isLoading && !prevData ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              maxWidth: {
                xs: "97%"
              },
              width: "100%"
            }}
          >
            <Typography variant="h4" gutterBottom>
              Calls
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2
              }}
            >
              <Button
                onClick={exportToCsv}
                type="button"
                color="primary"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                style={{
                  marginBottom: "10px"
                }}
              >
                Export to CSV <SystemUpdateAlt />
              </Button>
              {/* <Button
                onClick={() => handleOpenUpladDialog()}
                type="button"
                color="primary"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                style={{
                  marginBottom: "10px"
                }}
              >
                Upload <UploadFile />
              </Button> */}
            </Box>
          </Box>
          <FormSubmissionTable
            columnDefs={callTableColumnDefs}
            rowData={rowData || []}
            // onFilterChanged={onFilterChanged}
            // onSortChanged={onSortChanged}
            pageSize={pageSize}
            onGridReady={onGridReady}
            height="calc(100vh - 142px)"
            onCellClicked={(e) => {
              const { buyerId, id } = e.data;
              let url = `/buyers/${buyerId}/calls`;
              if (id) {
                url += `/${id}`;
              }
              window.open(url, "_blank");
            }}
          />
        </Box>
      )}
      <UploadListModal
        openDialog={openUploadDialog}
        setOpenDialog={setOpenUploadDialog}
        setOpenUploadErrorDialog={setOpenUploadErrorDialog}
      />
      <UploadListErrorModal
        openDialog={openUploadErrorDialog}
        setOpenDialog={setOpenUploadErrorDialog}
      />
    </Container>
  );
}
